import { CommonModule } from '@angular/common';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { Component, inject, OnInit, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { SharedModule } from '@shared/shared.module';
import { AuthService } from 'src/app/Auth/AuthService';
import { PlanService } from '@services/plan.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { PrivacyPolicyComponent } from '@pages/Outer/pages/privacy-policy/privacy-policy.component';
import { TermsConditionComponent } from '@pages/Outer/pages/terms-condition/terms-condition.component';
import { SecureStorageService } from '@services/secure-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import {
  WebQrPlan,
  WebQrSubscriptionService,
} from '@services/web-qr-subscription.service';

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [SharedModule, CommonModule, NzModalModule, ReactiveFormsModule, NzSelectModule],
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent implements OnInit {
  authService = inject(AuthService);
  sharedService = inject(SharedService);
  pagesService = inject(PagesService);
  planService = inject(PlanService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  renderer = inject(Renderer2);
  elementRef = inject(ElementRef);
  modal = inject(NzModalService);
  secureStorage = inject(SecureStorageService);
  translateService = inject(TranslateService);
  webQrSubscriptionService = inject(WebQrSubscriptionService);
  subscribedPlans: any = {};
  activeTabId: 'monthly' | 'annually' = 'monthly';
  selectedPlan: any = {};
  isVisible = false;
  monthlyPlans: any[] = [];
  annualPlans: any[] = [];
  enterprizePlans: any[] = [];
  isLoggedIn = false;
  isPaymentModal = false;
  selectedPaymentMethod: string = '';
  renewModal: boolean = false;
  enterprizeModal: boolean = false;
  enterprizeForm!: FormGroup;
  agreeTerms: boolean = false;
  user: any;
  remainingTime: any;
  isLoadingPlans: boolean = true;
  isFreePlanModalVisible = false;
  selectedFreePlan: any = null;
  private accessDeniedShown = false;
  planChangeType: 'upgrade' | 'downgrade' | '' = '';

  constructor(private fb: FormBuilder) {
    if (this.authService.isLoggedIn()) {
      this.isLoggedIn = true;
    }
    // ✅ Only check query params once on component initialization
    this.route.queryParams.subscribe((params) => {
      if (params['accessDenied'] === 'true' && !this.accessDeniedShown) {
        this.accessDeniedShown = true;
        this.sharedService.createNotification(
          'error',
          this.translateService.instant('subscription-page.expired-msg')
        );
        // Clear the query parameter to prevent showing the message again
        this.router.navigate([], {
          queryParams: { accessDenied: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
  }
  ngOnInit() {
    this.user = this.authService.getUserDetails();
    this.enterprizeForm = this.fb.group({
      plan_id: [null],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
        ],
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.email
        ]
      ],
      phone_number: [
        '',
        [
          Validators.required,
        ]
      ],
      company_name: ['', Validators.required],
      address: ['', Validators.required],
      payment_method: ['', Validators.required],
      details: ['', Validators.required]
    });

    this.prefillEnterpriseForm();

    if (this.isLoggedIn) {
      this.getSubscribedPlans();
    }
    this.getSubscriptionPlans();
    // Gated on the same flag as the section itself: hiding the cards while
    // still calling /web_qr/plans on every visit would be a request for
    // nothing.
    if (this.showWebQrPlans) {
      this.loadWebQrSection();
    }
  }

  // -- Web QR plans ---------------------------------------------------------
  // A separate subscription from the campaign plan above: its own plans, its
  // own billing period, bought on the /web_qr/* endpoints.

  webQrPlans: WebQrPlan[] = [];
  /** Current plan as /web_qr/plans reports it -- the only source it has. */
  webQrCurrentPlanId: number | null = null;
  webQrPlansLoading = false;
  webQrPlansError = '';
  /** Plan id currently being checked out, so only its button shows progress. */
  webQrBusyPlanId: number | null = null;

  /**
   * The Web QR plan whose purchase is going through the shared "Select Payment
   * Method" modal. Non-null means handlePaymentOk() should route to the
   * /web_qr/* endpoints instead of the campaign-plan ones.
   */
  webQrSelectedPlan: WebQrPlan | null = null;

  /**
   * Web QR plans are a dev-only section for now.
   *
   * Read from `environment.production`, which Angular swaps for the prod file
   * at build time (fileReplacements in angular.json), so nothing has to be
   * flipped by hand before a release.
   */
  get showWebQrPlans(): boolean {
    return !environment.production;
  }

  private loadWebQrSection(): void {
    this.loadWebQrPlans();
  }

  loadWebQrPlans(): void {
    this.webQrPlansLoading = true;
    this.webQrPlansError = '';
    // 'subscription' is the only plan type the Web QR list carries; asking for
    // a billing period ('monthly'/'yearly') returns nothing.
    this.webQrSubscriptionService.getPlans('subscription').subscribe({
      next: (result) => {
        this.webQrPlans = result.plans;
        this.webQrCurrentPlanId = result.currentPlanId;
        this.webQrPlansLoading = false;
      },
      error: (err: any) => {
        console.warn('[Subscription] Failed to load Web QR plans:', err);
        this.webQrPlans = [];
        this.webQrPlansError = err?.error?.message
          || this.translateService.instant('webQrPlans.load-failed');
        this.webQrPlansLoading = false;
      },
    });
  }

  /**
   * The Web QR plan held according to /purchased_plan, or null.
   *
   * This is the source that actually reports a fresh purchase: once a Web QR
   * plan is bought it becomes the client's active product, so purchased_plan
   * answers with active_product 'web_qr' and that plan's id -- while
   * /web_qr/plans may still send is_current false and /web_qr/my_subscription
   * has_subscription false. Without this a paid plan kept offering Subscribe.
   */
  private get purchasedWebQrPlan(): any | null {
    const purchased = this.subscribedPlans;
    if (!purchased || typeof purchased !== 'object') return null;
    if (String(purchased.active_product ?? '').toLowerCase() !== 'web_qr') return null;
    // An expired plan is no longer held: the cards go back to offering one.
    if (purchased.isExpired === true) return null;
    return purchased;
  }

  /**
   * The plan the client is already on, if any. Both sources that can say so
   * are asked, and whichever answers is enough to mark the card.
   */
  isCurrentWebQrPlan(plan: WebQrPlan): boolean {
    // The plan row says so itself, with current_plan_id kept as a fallback in
    // case a row arrives without the flag.
    if (plan.isCurrent) return true;
    if (this.webQrCurrentPlanId && this.webQrCurrentPlanId === plan.id) return true;

    const purchased = this.purchasedWebQrPlan;
    if (!purchased) return false;

    // Matched on id, then on name: purchased_plan calls the plan `type`, and
    // its plan_id is trusted to be the same plan table the cards come from --
    // the name settles it if that ever stops holding.
    const purchasedId = Number(purchased.plan_id ?? 0) || 0;
    if (purchasedId && purchasedId === plan.id) return true;

    const purchasedName = String(purchased.type ?? '').trim().toLowerCase();
    return !!purchasedName && purchasedName === plan.name.trim().toLowerCase();
  }

  /**
   * The card the client's Web QR plan corresponds to, or null.
   *
   * Deliberately derived from the cards rather than from a "does a
   * subscription exist" flag. Those are not the same question: a source can
   * report a subscription whose plan matches no card on screen -- an id from
   * another plan list, a plan no longer offered, a record with no plan id at
   * all. Answering the flag would then hide every button and leave the client
   * with no action anywhere.
   */
  private get currentWebQrPlanCard(): WebQrPlan | null {
    return this.webQrPlans.find((plan) => this.isCurrentWebQrPlan(plan)) ?? null;
  }

  /**
   * Where a plan sits relative to the one the client holds, as the campaign
   * cards report it: the same plan, a dearer one, or a cheaper one.
   *
   * Returns '' when no direction can honestly be claimed. That happens when
   * the two plans are priced in different currencies -- comparing AED 2,500
   * with USD 200 as bare numbers would call the dearer plan a downgrade. The
   * API sends no rank or tier to order them by, and no conversion rate, so
   * such a card is offered as a plain purchase instead of a made-up direction.
   */
  webQrPlanComparison(plan: WebQrPlan): 'upgrade' | 'downgrade' | 'same' | '' {
    const current = this.currentWebQrPlanCard;
    if (!current) return '';
    if (current.id === plan.id) return 'same';
    if (!current.currency || current.currency !== plan.currency) return '';
    if (plan.priceAmount === current.priceAmount) return 'same';
    return plan.priceAmount > current.priceAmount ? 'upgrade' : 'downgrade';
  }

  /**
   * What a Web QR card's button reads. Mirrors the campaign cards: the plan in
   * force offers Renew, the others Upgrade or Downgrade, and with no plan held
   * every card offers Subscribe.
   */
  webQrPlanActionLabel(plan: WebQrPlan): string {
    if (this.isCurrentWebQrPlan(plan)) return 'webQrPlans.renew';

    switch (this.webQrPlanComparison(plan)) {
      case 'upgrade': return 'Plan&Price.upgradeBtn';
      case 'downgrade': return 'Plan&Price.downgradeBtn';
      // 'same' without being the current card means an equally priced
      // alternative -- a switch, not a step in either direction.
      default: return 'webQrPlans.subscribe';
    }
  }

  /**
   * How a plan's period reads on the card. The API sends a bare word
   * ('quarter'), so a known one gets a translated label and anything else
   * falls back to the raw value rather than showing nothing.
   */
  webQrPricePerLabel(plan: WebQrPlan): string {
    const per = (plan.pricePer || '').toLowerCase();
    const known: Record<string, string> = {
      month: 'webQrPlans.per-month',
      monthly: 'webQrPlans.per-month',
      quarter: 'webQrPlans.per-quarter',
      quarterly: 'webQrPlans.per-quarter',
      year: 'webQrPlans.per-year',
      yearly: 'webQrPlans.per-year',
      annual: 'webQrPlans.per-year',
    };
    return known[per] ? this.translateService.instant(known[per]) : plan.pricePer;
  }

  isWebQrPlanBusy(plan: WebQrPlan): boolean {
    return this.webQrBusyPlanId === plan.id;
  }

  /**
   * Starts a Web QR purchase.
   *
   * Deliberately the same journey as the campaign plans: it opens the shared
   * "Select Payment Method" modal rather than checking out straight away, so
   * cash and online are offered the same way and the terms checkbox is honoured
   * in one place. handlePaymentOk() then routes to the Web QR endpoints.
   */
  onWebQrPlanAction(plan: WebQrPlan): void {
    if (!this.requireLoginForWebQr()) return;

    // The modal is shared, so it must not open carrying the previous choice.
    this.selectedPaymentMethod = '';
    this.agreeTerms = false;
    this.webQrSelectedPlan = plan;
    this.isPaymentModal = true;
  }

  /**
   * The Web QR half of handlePaymentOk(). Returns false when this is not a Web
   * QR purchase, so the campaign-plan path continues untouched.
   */
  private handleWebQrPaymentOk(): boolean {
    const plan = this.webQrSelectedPlan;
    if (!plan) return false;

    this.webQrSelectedPlan = null;

    if (this.selectedPaymentMethod === 'online') {
      this.checkoutWebQrPlan(plan);
    } else if (this.selectedPaymentMethod === 'cash') {
      // Same confirm step the campaign plans use before a cash request.
      this.modal.confirm({
        nzTitle: this.translateService.instant('subscription-page.cash-payment'),
        nzContent: this.translateService.instant('subscription-page.cash-payment-confirm-desc'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOkText: this.translateService.instant('subscription-page.proceed'),
        nzCancelText: this.translateService.instant('subscription-page.cancel'),
        nzOnOk: () => this.requestWebQrCashPayment(plan),
      });
    }
    return true;
  }

  /** Online payment: subscribe, or renew when it is already the client's plan. */
  private checkoutWebQrPlan(plan: WebQrPlan): void {
    const base = this.sharedService.getBaseUrl();
    // flow=web_qr tells the return page which confirm endpoint to call: it is
    // the same success page as the campaign plans, but a Web QR session has to
    // be confirmed on /web_qr/confirm_payment, not on CHECK_PAYMENT.
    const successUrl = base + '/auth/payment-approved?flow=web_qr&session_id={CHECKOUT_SESSION_ID}';
    const cancelUrl = base + '/home/subscription';
    const renewing = this.isCurrentWebQrPlan(plan);

    this.webQrBusyPlanId = plan.id;
    this.sharedService.showLoader();

    const request = renewing
      ? this.webQrSubscriptionService.renew(plan.id, successUrl, cancelUrl)
      : this.webQrSubscriptionService.subscribe(plan.id, successUrl, cancelUrl);

    request.subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        const checkoutUrl = res?.checkout_url || res?.url || res?.payment_url;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        // No url means the API accepted it without a payment step -- reflect
        // the new state rather than leaving a dead button on screen.
        this.loadWebQrPlans();
        this.sharedService.createNotification(
          'warning',
          this.translateService.instant('subscription-page.checkout-url-not-found')
        );
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        this.sharedService.createNotification(
          'error',
          err?.error?.message || this.translateService.instant('webQrPlans.checkout-failed')
        );
      },
    });
  }

  /**
   * Cash payment: records a request. It activates nothing -- an admin approves
   * it -- so the list is refreshed to show it as pending.
   */
  private requestWebQrCashPayment(plan: WebQrPlan): void {
    this.webQrBusyPlanId = plan.id;
    this.sharedService.showLoader();

    this.webQrSubscriptionService.requestCashPayment(plan.id, '').subscribe({
      next: () => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        this.sharedService.createNotification(
          'success',
          this.translateService.instant('webQrPlans.cash-requested')
        );
        this.loadWebQrPlans();
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        this.sharedService.createNotification(
          'error',
          err?.error?.message || this.translateService.instant('webQrPlans.cash-failed')
        );
      },
    });
  }

  /**
   * Plans are readable while signed out, but nothing may be bought -- the same
   * prompt the campaign plans use.
   */
  private requireLoginForWebQr(): boolean {
    if (this.isLoggedIn) return true;
    this.modal.warning({
      nzTitle: this.translateService.instant('subscription-page.login-required-title'),
      nzContent: this.translateService.instant('subscription-page.login-required-desc'),
      nzCentered: true,
      nzClassName: 'contest-styled-modal contest-modal-warning',
      nzOkText: this.translateService.instant('subscription-page.login-btn'),
      nzOnOk: () => this.router.navigate(['auth/login']),
    });
    return false;
  }

  freePlanApi(planId: number) {
    const user = this.authService.getUserDetails();
    if (!user) return;
    const payload = {
      user_id: user.id,
      plan_id: planId
    };
    this.sharedService.showLoader();
    this.pagesService.create('FREE_PLAN', payload).subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'success',
          this.translateService.instant('subscription-page.free-plan-subscribed-success')
        );

        // ✅ Update premimum_user flag in localStorage
        const userDetails = this.authService.getUserDetails();
        if (userDetails) {
          userDetails.premimum_user = true;
          void this.secureStorage.setItem('userDetails', userDetails);
        }

        this.sharedService.updatedUserData.next(true);
        this.getSubscribedPlans();
      },
      error: (err: any) => {
        this.sharedService.hideLoader();

        // Check multiple possible error structures
        const errorCode = err?.error?.code || err?.code;
        const errorMessage = err?.error?.message || err?.message;

        if (errorCode === 'ERROR_FATAL') {
          this.sharedService.createNotification(
            'error',
            errorMessage || this.translateService.instant('subscription-page.free-plan-subscribe-failed')
          );
        } else {
          // Show generic error message if it's a different error
          this.sharedService.createNotification(
            'error',
            errorMessage || this.translateService.instant('subscription-page.free-plan-subscribe-failed')
          );
        }
      }
    });
  }
  // free plan timmer function
  isFreePlan(): boolean {
    if (!this.subscribedPlans || !this.subscribedPlans.sub_plan) {
      return false;
    }
    const subPlan = this.subscribedPlans.sub_plan;
    const bothPricesFree = subPlan.monthly_price === 0 && subPlan.yearly_price === 0;
    const descriptionContainsFree = subPlan.description &&
      subPlan.description.toLowerCase().includes('free');
    const planNameContainsFree = subPlan.plan_name &&
      subPlan.plan_name.toLowerCase().includes('free');
    return bothPricesFree || descriptionContainsFree || planNameContainsFree;
  }
  showModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    this.isVisible = false;
    // Not a Web QR purchase: clear any plan left over from that flow.
    this.webQrSelectedPlan = null;
    this.isPaymentModal = true;
    this.planChangeType = '';
  }
  handlePaymentOk(): void {
    if (!this.selectedPaymentMethod) {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('subscription-page.select-payment-method-required')
      );
      return;
    }
    this.isPaymentModal = false;

    // A Web QR purchase uses the same modal but different endpoints.
    if (this.handleWebQrPaymentOk()) return;

    const isRenewFlow = !!this.selectedPlan?.isRenewal;

    if (isRenewFlow) {
      this.renewCurrentPlan(this.selectedPlan.plan_id, this.selectedPaymentMethod);
      return;
    }

    if (this.selectedPaymentMethod === 'online') {
      this.subscribAPlan(this.selectedPlan.plan_id);
    } else if (this.selectedPaymentMethod === 'cash') {
      this.modal.confirm({
        nzTitle: this.translateService.instant('subscription-page.cash-payment'),
        nzContent: this.translateService.instant('subscription-page.cash-payment-confirm-desc'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOkText: this.translateService.instant('subscription-page.proceed'),
        nzCancelText: this.translateService.instant('subscription-page.cancel'),
        nzOnOk: () => {
          this.cashPayment(this.selectedPlan.plan_id);
        }
      });
    }
  }
  cashPayment(plan_id: string) {
    if (!this.isLoggedIn) {
      this.modal.warning({
        nzTitle: this.translateService.instant('subscription-page.login-required-title'),
        nzContent: this.translateService.instant('subscription-page.login-required-desc'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-warning',
        nzOkText: this.translateService.instant('subscription-page.login-btn'),
        nzOnOk: () => {
          this.router.navigate(['auth/login']);
        }
      });
      return;
    }
    const payload = {
      plan_id,
      type: this.activeTabId
    };
    this.sharedService.showLoader();
    this.pagesService.create('CASH_PAYMENT', payload).subscribe({
      next: (Response: any) => {
        this.sharedService.hideLoader();
        this.isPaymentModal = false;
        // this.sharedService.createNotification(
        //   'success',
        //   'Cash payment request submitted successfully. Our team will contact you soon.'
        // );
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.modal.error({
          nzTitle: this.translateService.instant('subscription-page.error-title'),
          nzContent: this.translateService.instant('subscription-page.cash-payment-init-failed'),
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-error',
          nzOkText: this.translateService.instant('common.ok')
        });
      },
    });
  }
  handleCancel(): void {
    this.isVisible = false;
    this.selectedPlan = null;
    this.planChangeType = '';
  }
  handleRenewCancel(): void {
    this.renewModal = false;
  }
  handleEnterprizeCancel(): void {
    this.enterprizeModal = false;
    this.enterprizeForm.reset();
  }
  handleRenewOk(): void {
    this.renewModal = false;
    // Not a Web QR purchase: clear any plan left over from that flow.
    this.webQrSelectedPlan = null;
    this.isPaymentModal = true;
  }
  handleEnterprizeOk(): void {
    if (!this.enterprizeForm.valid) {
      this.sharedService.hideLoader();
      this.enterprizeForm.markAllAsTouched();
      return;
    }
    const form = this.enterprizeForm;
    const basePayload = {
      plan_id: form.get('plan_id')?.value,
      name: form.get('name')?.value,
      email: form.get('email')?.value,
      phone_number: form.get('phone_number')?.value,
      details: form.get('details')?.value,
      company_name: form.get('company_name')?.value,
      address: form.get('address')?.value,
      payment_method: form.get('payment_method')?.value,
    };
    this.sharedService.showLoader();
    this.pagesService.update('ENTERPRISE_PLAN', basePayload).subscribe({
      next: () => {
        this.sharedService.createNotification(
          'success',
          this.translateService.instant('subscription-page.enterprise-request-success')
        );
        this.enterprizeForm.reset();
        this.enterprizeModal = false;
        this.sharedService.hideLoader();
      },
      error: (err: any) => {
        const backendMessage = err?.error?.message || err?.message || '';
        this.sharedService.createNotification(
          'error',
          backendMessage || this.translateService.instant('subscription-page.enterprise-request-failed')
        );
        this.sharedService.hideLoader();
      },
    });
  }
  handlePaymentCancel(): void {
    this.isPaymentModal = false;
    this.selectedPaymentMethod = '';
    this.webQrSelectedPlan = null;
  }
  subscribe(plan_id: string, type: string, mode: string, price: string) {
    if (!this.isLoggedIn) {
      this.sharedService.createNotification(
        'warning',
        this.translateService.instant('subscription-page.subscribe-login-warning')
      );
      this.router.navigate(['auth/login']);
      return;
    }
    this.selectedPlan = { plan_id, type, mode, price };
    if (this.subscribedPlans && this.subscribedPlans.type) {
      this.isVisible = true;
    } else {
      // Not a Web QR purchase: clear any plan left over from that flow.
      this.webQrSelectedPlan = null;
      this.isPaymentModal = true;
    }
  }
  subscribAPlan(plan_id: string) {
    const type = {
      plan_id,
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/home/subscription`,
      type: this.activeTabId,
    };
    this.sharedService.showLoader();
    this.authService.login('CHOOSE_SUBSCRIPTION_PLAN', type).subscribe({
      next: (Response: any) => {
        setTimeout(() => {
          this.sharedService.hideLoader();
          if (Response.checkout_url) {
            window.location.href = Response.checkout_url;
          } else {
            this.sharedService.createNotification(
              'error',
              this.translateService.instant('subscription-page.checkout-url-not-found')
            );
          }
        }, 1000);
        const alertsJson = localStorage.getItem('app_alerts');
        if (alertsJson) {
          let alerts = JSON.parse(alertsJson);
          alerts = alerts.filter(
            (alert: any) => alert.action !== 'subscription'
          );
          localStorage.setItem('app_alerts', JSON.stringify(alerts));
        }
        this.sharedService.closeAlertByAction('subscription');
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          this.translateService.instant('subscription-page.subscribe-init-failed')
        );
      },
    });
  }
  getSubscribedPlans() {
    this.isLoadingPlans = true;
    this.sharedService.showLoader();
    this.authService.getAll('PURCHASED_PLAN').subscribe({
      next: (Response: any) => {
        this.subscribedPlans = Response || null;

        if (this.subscribedPlans) {
          const isExpiredFromApi = typeof this.subscribedPlans?.isExpired === 'boolean'
            ? this.subscribedPlans.isExpired
            : null;

          // If API explicitly marks expired, honor it; otherwise calculate days left
          this.remainingTime = isExpiredFromApi === true
            ? { days: 0, expired: true }
            : this.calculateRemainingTime(this.subscribedPlans);

          if (this.remainingTime.expired) {
            this.sharedService.createNotification(
              'warning',
              this.translateService.instant('subscription-page.subscription-expired-msg')
            );
          }
        }

        const userPlan = Response?.report_view?.toLowerCase() || 'none';
        this.sharedService.setPlanType(userPlan);
        this.isLoadingPlans = false;
        this.sharedService.hideLoader();
        this.setActiveTabBasedOnSubscription();
      },
      error: (error: any) => {
        this.isLoadingPlans = false;
        this.sharedService.hideLoader();

        if (error && error.message === 'User is not subscribed yet') {
          this.subscribedPlans = {};
          this.sharedService.setPlanType('none');
        }
      },
    });
  }

  getSubscriptionPlans() {
    const getOrderIndex = (price: any): number => {
      if (price == null) return Number.MAX_SAFE_INTEGER;
      if (typeof price === 'string') {
        const lower = price.toLowerCase().trim();
        if (lower === 'free') return 0;
        if (lower.includes('contact')) return Number.MAX_SAFE_INTEGER;
        const numeric = parseFloat(lower.replace(/[^0-9.]/g, ''));
        return isNaN(numeric) ? Number.MAX_SAFE_INTEGER : numeric;
      }
      return price;
    };
    const sortPlans = (plans: any[], priceKey: 'monthly_price' | 'yearly_price') => {
      return plans.sort((a: any, b: any) => {
        // Enterprise always last
        if (a.isEnterprise !== b.isEnterprise) {
          return a.isEnterprise ? 1 : -1;
        }
        // Then sort by monthly/yearly price
        return getOrderIndex(a?.[priceKey]) - getOrderIndex(b?.[priceKey]);
      });
    };
    this.sharedService.showLoader();
    this.pagesService.getAll('GET_SUBSCRIPTION_PLAN', {
      page: 1,
      per_page: 1000
    }).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        const plans = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];

        if (plans.length > 0) {
          const activePlans = plans.filter(
            (plan: any) => plan?.status?.toLowerCase() === 'active'
          );
          // Use monthly_price & yearly_price instead of mode
          this.monthlyPlans = sortPlans(activePlans, 'monthly_price');
          this.annualPlans = sortPlans(activePlans, 'yearly_price');

          // Enterprise plans separately if you want
          this.enterprizePlans = activePlans.filter((plan: any) => plan.isEnterprise);
          // ✅ Yahan expiry check karo
          if (this.remainingTime?.expired) {
            this.sharedService.createNotification(
              'warning',
              this.translateService.instant('subscription-page.subscription-expired-warning')
            );
          }
        } else {
          this.sharedService.createNotification('error', this.translateService.instant('subscription-page.no-plans-available'));
        }
      },
      error: () => {
        this.sharedService.hideLoader();
      },
    });
  }
  isEnterprizePlan(plan: any): boolean {
    return plan?.isEnterprise === true;
  }

  /**
   * Whether a plan includes the WhatsApp Companion.
   *
   * Answered from `whatsapp_companion` alone. It used to fall back to guessing
   * from the plan's name and price when the flag was missing, which reported
   * the feature as included on any plan merely called "pro" or "growth" --
   * true or not. A missing flag now reads the same as a false one: not
   * included, and the row is left off the card entirely.
   */
  hasWhatsappCompanion(plan: any): boolean {
    if (!plan) return false;
    return plan.whatsapp_companion === true || plan.sub_plan?.whatsapp_companion === true;
  }
  setActiveTabBasedOnSubscription() {
    if (this.subscribedPlans && this.subscribedPlans.mode) {
      if (
        this.subscribedPlans.mode.toLowerCase() === 'annually' ||
        this.subscribedPlans.mode.toLowerCase() === 'yearly'
      ) {
        this.activeTabId = 'annually';
      } else {
        this.activeTabId = 'monthly';
      }
    } else {
      this.activeTabId = 'monthly';
    }
    this.updateBootstrapTabActivation();
  }
  updateBootstrapTabActivation() {
    if (typeof window !== 'undefined' && this.elementRef) {
      setTimeout(() => {
        const monthlyTabButton = this.elementRef.nativeElement.querySelector('#monthly-tab');
        const yearlyTabButton = this.elementRef.nativeElement.querySelector('#yearly-tab');
        const enterprizeTabButton = this.elementRef.nativeElement.querySelector('#enterprize-tab');
        const monthlyTabPane = this.elementRef.nativeElement.querySelector('#monthly');
        const yearlyTabPane = this.elementRef.nativeElement.querySelector('#yearly');
        const enterprizeTabPane = this.elementRef.nativeElement.querySelector('#enterprize');
        const tabs = [
          { id: 'monthly', button: monthlyTabButton, pane: monthlyTabPane },
          { id: 'annually', button: yearlyTabButton, pane: yearlyTabPane },
          { id: 'enterprize', button: enterprizeTabButton, pane: enterprizeTabPane },
        ];
        tabs.forEach(tab => {
          const isActive = this.activeTabId === tab.id;
          if (tab.button && tab.pane) {
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.button, 'active');
            this.renderer.setAttribute(tab.button, 'aria-selected', isActive.toString());
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.pane, 'show');
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.pane, 'active');
          }
        });
      }, 0);
    }
  }
  selectTab(tabId: 'monthly' | 'annually') {
    this.activeTabId = tabId;
    this.updateBootstrapTabActivation();
  }
  isCurrentPlan(id: number, mode: string): boolean {
    if (this.remainingTime?.expired) return false; // ✅ yahan se badge chala jayega
    const currentType = this.subscribedPlans?.plan_id;
    const currentMode = this.subscribedPlans?.mode?.toLowerCase();
    return currentType === id && currentMode === mode.toLowerCase();
  }
  renewSubscribtion() {
    this.renewModal = true
  }

  isPlanExpired(): boolean {
    return !!(this.subscribedPlans?.isExpired === true || this.remainingTime?.expired);
  }

  isSamePlan(plan: any): boolean {
    return this.isCurrentPlanId(plan?.id);
  }

  private getCurrentPlanId(): number | null {
    const rawPlanId =
      this.subscribedPlans?.plan_id ??
      this.subscribedPlans?.subscription_plan_id ??
      this.subscribedPlans?.sub_plan?.id ??
      this.subscribedPlans?.sub_plan?.plan_id;

    if (rawPlanId === null || rawPlanId === undefined || rawPlanId === '') {
      return null;
    }

    const planId = Number(rawPlanId);
    return Number.isFinite(planId) ? planId : null;
  }

  private isCurrentPlanId(planId: any): boolean {
    const currentPlanId = this.getCurrentPlanId();
    const candidatePlanId = Number(planId);
    return currentPlanId !== null && Number.isFinite(candidatePlanId) && currentPlanId === candidatePlanId;
  }

  // A free plan (price 0 on both cycles, or a plan named "free")
  isFreePlanCard(plan: any): boolean {
    if (!plan) return false;
    const bothPricesFree = Number(plan.monthly_price) === 0 && Number(plan.yearly_price) === 0;
    const planNameFree = !!plan.plan_name && plan.plan_name.toLowerCase().includes('free');
    return bothPricesFree || planNameFree;
  }

  // Only treat the free plan as active when the active subscription itself is free.
  // The backend/user free-plan flag can be stale after a user upgrades.
  isFreePlanActive(): boolean {
    if (!this.hasActiveSubscription()) {
      return false;
    }

    const currentPlan = this.subscribedPlans?.sub_plan;
    if (currentPlan) {
      return this.isFreePlanCard(currentPlan);
    }

    const cardPlan = [...this.monthlyPlans, ...this.annualPlans].find((plan) => this.isSamePlan(plan));
    if (cardPlan) {
      return this.isFreePlanCard(cardPlan);
    }

    const currentPlanName = String(
      this.subscribedPlans?.plan_name ||
      this.subscribedPlans?.type ||
      ''
    ).toLowerCase();

    return currentPlanName.includes('free');
  }

  shouldShowRenew(plan: any): boolean {
    // A free plan never needs renewing, so it should never show the Renew action.
    return this.isLoggedIn && this.isSamePlan(plan) && !plan?.isEnterprise && !this.isFreePlanCard(plan);
  }

  getPlanActionLabel(plan: any): string {
    if (this.shouldShowRenew(plan)) return 'subscription-page.renew';
    if (this.getPlanComparison(plan.id) === 'same') return 'Plan&Price.currentPlanBtn';

    if (!this.hasActiveSubscription()) return 'subscription-page.subscribe';

    return this.getPlanComparison(plan.id) === 'upgrade' ? 'Plan&Price.upgradeBtn' : 'Plan&Price.downgradeBtn';
  }

  private titleCase(value: string): string {
    return value.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }

  getPlanDisplayName(plan: any): string {
    const rawName = (plan?.plan_name || plan?.type || '').toString().trim();
    if (!rawName) return '';

    const lower = rawName.toLowerCase();
    const slug = lower.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    // 1. Direct translation key from i18n
    const directKey = `subscription-page.plan-names.${lower}`;
    const directTranslated = this.translateService.instant(directKey);
    if (directTranslated && directTranslated !== directKey) {
      return directTranslated;
    }

    // 2. Slug translation key from i18n
    const slugKey = `subscription-page.plan-names.${slug}`;
    const slugTranslated = this.translateService.instant(slugKey);
    if (slugTranslated && slugTranslated !== slugKey) {
      return slugTranslated;
    }

    // 3. Fallback dictionary when Arabic is active
    const currentLang = this.translateService.currentLang || localStorage.getItem('lang') || 'en';
    if (currentLang === 'ar') {
      const arabicMap: Record<string, string> = {
        'activation': 'التفعيل',
        'reach': 'الوصول',
        'campaign': 'الحملة',
        'growth - no web access': 'النمو - بدون وصول للويب',
        'growth-no-web-access': 'النمو - بدون وصول للويب',
        'pro - webqr': 'الاحترافية - ويب كيو آر',
        'pro-webqr': 'الاحترافية - ويب كيو آر',
        'starter': 'المبتدئة',
        'growth': 'النمو',
        'pro': 'الاحترافية',
        'elite': 'النخبة',
        'free': 'المجانية',
        'standard': 'قياسي',
        'silver': 'فضي',
        'gold': 'ذهبي',
        'premium': 'بريميوم',
        'basic': 'أساسية',
        'enterprise': 'المؤسسات',
        'webqr': 'ويب كيو آر',
        'scale': 'التوسع'
      };

      if (arabicMap[lower]) return arabicMap[lower];
      if (arabicMap[slug]) return arabicMap[slug];
    }

    return this.titleCase(rawName);
  }

  renewCurrentPlan(plan: any, paymentMethod: string = 'online') {
    const user = this.authService.getUserDetails();

    if (!user?.id) {
      this.sharedService.createNotification('warning', this.translateService.instant('subscription-page.renew-login-warning'));
      this.router.navigate(['auth/login']);
      return;
    }

    const payload = {
      plan_id: this.subscribedPlans?.plan_id || plan?.id,
      user_id: user.id,
      billing_type: this.activeTabId === 'annually' ? 'annually' : 'monthly',
      payment_method: paymentMethod || 'online',
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/auth/payment-rejected`
    };

    this.sharedService.showLoader();
    this.pagesService.create('RENEWAL_PAYMENT_LINK', payload).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        if (response?.checkout_url) {
          window.location.href = response.checkout_url;
          return;
        }
        if (payload.payment_method === 'cash') {
          this.modal.success({
            nzTitle: this.translateService.instant('subscription-page.renew-request-submitted-title'),
            nzContent: this.translateService.instant('subscription-page.renew-request-submitted-desc'),
            nzCentered: true,
            nzClassName: 'contest-styled-modal contest-modal-success',
            nzOkText: this.translateService.instant('common.ok')
          });
          return;
        }
        this.sharedService.createNotification('error', this.translateService.instant('subscription-page.checkout-url-not-found'));
      },
      error: () => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', this.translateService.instant('subscription-page.renew-init-failed'));
      }
    });
  }

  handleClick(plan: any) {
    if (this.shouldShowRenew(plan)) {
      const mode = this.activeTabId === 'annually' ? 'annually' : 'monthly';
      const renewCycle = mode === 'annually' ? 'yearly' : 'monthly';
      let price = this.getTotalPrice(plan, renewCycle);

      // Use the same price logic as the card display
      if (this.hasRenewalDiscount(plan, renewCycle)) {
        price = this.getRenewalDiscountAmount(plan, renewCycle) + this.getEffectiveSdkPrice(plan);
      } else if (plan.discount_type && plan.discount_value) {
        price = this.getDiscountedPrice(price, plan.discount_type, plan.discount_value);
      }

      this.selectedPlan = {
        plan_id: plan.id,
        type: plan.type,
        plan_name: plan.plan_name,
        mode: mode,
        price: price,
        currency: plan.currency || 'USD',
        isRenewal: true
      };
      this.renewModal = true;
      return;
    }

    if (plan.isEnterprise) {
      this.EnterprizeSubscribtion();
      this.enterprizeForm.get('plan_id')?.setValue(plan?.id);
      this.prefillEnterpriseForm();
    }
    // Free plan case
    else if (
      (plan.monthly_price === 0 && plan.yearly_price === 0) ||
      plan.plan_name.toLowerCase().includes('free')
    ) {
      this.selectedFreePlan = plan;
      this.isFreePlanModalVisible = true;
    }
    else {
      // Determine the mode and price based on active tab
      const mode = this.activeTabId === 'annually' ? 'annually' : 'monthly';
      const cycle = mode === 'annually' ? 'yearly' : 'monthly';
      let price = this.getTotalPrice(plan, cycle);

      // Use the same price logic as the card display
      if (this.hasRenewalDiscount(plan, cycle)) {
        price = this.getRenewalDiscountAmount(plan, cycle) + this.getEffectiveSdkPrice(plan);
      } else if (plan.discount_type && plan.discount_value) {
        price = this.getDiscountedPrice(price, plan.discount_type, plan.discount_value);
      }

      const hasValidSubscription = this.subscribedPlans &&
        Object.keys(this.subscribedPlans).length > 0 &&
        this.subscribedPlans.plan_id !== undefined;
      if (hasValidSubscription && !this.remainingTime?.expired) {
        this.selectedPlan = {
          plan_id: plan.id,
          type: plan.type,
          plan_name: plan.plan_name,
          mode: mode,
          price: price,
          monthly_price: plan.monthly_price,
          yearly_price: plan.yearly_price,
          currency: plan.currency || 'USD'
        };

        const comparison = this.getPlanComparison(plan.id);
        this.planChangeType = comparison === 'upgrade' ? 'upgrade' :
          comparison === 'downgrade' ? 'downgrade' : '';

        this.isVisible = true;
      }
      else {
        this.selectedPlan = {
          plan_id: plan.id,
          type: plan.type,
          plan_name: plan.plan_name,
          mode: mode,
          price: price
        };
        // Not a Web QR purchase: clear any plan left over from that flow.
        this.webQrSelectedPlan = null;
        this.isPaymentModal = true;
      }
    }
  }
  handleFreePlanOk() {
    this.isFreePlanModalVisible = false;
    if (this.selectedFreePlan?.id) {
      this.freePlanApi(this.selectedFreePlan.id); // Pass the dynamic ID
    }
  }
  handleFreePlanCancel() {
    this.isFreePlanModalVisible = false;
  }
  EnterprizeSubscribtion() {
    this.enterprizeModal = true
  }

  private prefillEnterpriseForm(): void {
    if (!this.isLoggedIn) return;
    const user = this.user || this.authService.getUserDetails();
    if (!user || !this.enterprizeForm) return;

    const companyName =
      user.isCompany?.[0]?.name ||
      user._company_as_client?.name ||
      user.company_name ||
      user.company?.name ||
      '';

    this.enterprizeForm.patchValue({
      name: user.name || user.full_name || '',
      company_name: companyName,
      email: user.email || '',
      phone_number: user.phone_number || user.phone || user.mobile || ''
    }, { emitEvent: false });
  }
  hasActiveSubscription(): boolean {
    return this.getCurrentPlanId() !== null && !this.remainingTime?.expired;
  }

  getPlanComparison(planId: number): 'upgrade' | 'downgrade' | 'same' | '' {
    if (this.remainingTime?.expired) {
      return '';
    }
    const allPlans = [...this.monthlyPlans, ...this.annualPlans];
    const newPlan = allPlans.find(p => p.id === planId);
    const currentSubscription = this.subscribedPlans;
    const currentPlanId = this.getCurrentPlanId();
    const hasValidSubscription = currentPlanId !== null;
    if (!newPlan || !hasValidSubscription) return 'upgrade';
    if (currentPlanId === Number(planId)) {
      const currentMode = currentSubscription.mode?.toLowerCase();
      const selectedMode = this.activeTabId === 'annually' ? 'annually' : 'monthly';

      if ((currentMode === 'annually' || currentMode === 'yearly') && selectedMode === 'annually') {
        return 'same';
      } else if (currentMode !== 'annually' && currentMode !== 'yearly' && selectedMode === 'monthly') {
        return 'same';
      }
    }
    if (newPlan.isEnterprise || currentSubscription.sub_plan?.isEnterprise) {
      if (currentPlanId === Number(planId)) {
        return 'same';
      }
      return 'upgrade';
    }
    const selectedCycle: 'monthly_price' | 'yearly_price' =
      this.activeTabId === 'annually' ? 'yearly_price' : 'monthly_price';
    const subscribedCycle: 'monthly_price' | 'yearly_price' =
      (currentSubscription.mode?.toLowerCase() === 'annually' ||
        currentSubscription.mode?.toLowerCase() === 'yearly')
        ? 'yearly_price'
        : 'monthly_price';
    const currentPlan = allPlans.find(p => this.isCurrentPlanId(p.id)) || currentSubscription.sub_plan;
    const currentPrice = this.getPriceValue(currentPlan?.[subscribedCycle]);
    const newPrice = this.getPriceValue(newPlan[selectedCycle]);

    if (subscribedCycle !== selectedCycle) {
      if (selectedCycle === 'yearly_price') {
        return newPrice >= currentPrice ? 'upgrade' : 'downgrade';
      } else {
        return newPrice <= currentPrice ? 'downgrade' : 'upgrade';
      }
    }
    if (newPrice > currentPrice) return 'upgrade';
    if (newPrice < currentPrice) return 'downgrade';
    return currentPlanId === Number(planId) ? 'same' : 'upgrade';
  }
  private getPriceValue(price: any): number {
    if (price == null || price === undefined) return 0;

    if (typeof price === 'string') {
      const lower = price.toLowerCase().trim();

      if (lower === 'free' || lower === '') return 0;
      if (lower.includes('contact') || lower.includes('enterprise')) return Number.MAX_SAFE_INTEGER;

      const numeric = parseFloat(lower.replace(/[^0-9.]/g, ''));
      return isNaN(numeric) ? 0 : numeric;
    }
    if (typeof price === 'number') return price;
    return 0;
  }
  calculateRemainingTime(plan: any): {
    days: number;
    expired: boolean;
  } {
    if (!plan?.start_date || !plan?.end_date) {
      return { days: 0, expired: true };
    }

    const now = new Date();
    const endDate = new Date(plan.end_date);

    // ✅ End date ko midnight set karein taake pure din ka calculation ho
    endDate.setHours(23, 59, 59, 999);

    const diff = endDate.getTime() - now.getTime();

    if (diff <= 0) {
      return { days: 0, expired: true };
    }

    // ✅ Total seconds calculate karein
    const totalSeconds = Math.floor(diff / 1000);

    // ✅ Days calculate karein
    const days = Math.floor(totalSeconds / (60 * 60 * 24));

    return { days, expired: false };
  }

  getEffectiveSdkPrice(plan: any): number {
    const sdkPrice = Number(plan?.sdk_price || 0);
    if (!sdkPrice) return 0;
    // If sdk_billing_cycle is "one_time" and user already has this plan, don't add sdk_price again
    if (plan?.sdk_billing_cycle === 'one_time' && this.isSamePlan(plan) && !this.isPlanExpired()) {
      return 0;
    }
    return sdkPrice;
  }

  getTotalPrice(plan: any, cycle: 'monthly' | 'yearly'): number {
    const basePrice = cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);
    const sdkPrice = this.getEffectiveSdkPrice(plan);
    return basePrice + sdkPrice;
  }

  getDiscountedPrice(price: number, discountType: string, discountValue: number): number {
    if (!discountType || !discountValue) return price;

    if (discountType === 'percentage') {
      return price - (price * discountValue / 100);
    } else if (discountType === 'fixed') {
      return price - discountValue;
    }
    return price;
  }

  formatPositive(value: any): any {
    if (value === null || value === undefined) return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return Math.abs(num);
  }

  private getActiveRenewalDiscount(plan: any): any | null {
    const discounts = plan?.subscription_renewal_discount_of_user_of_billing_payment;
    if (!Array.isArray(discounts) || discounts.length === 0) return null;

    const now = Date.now();
    const active = discounts.find((item: any) => {
      const status = String(item?.status || '').toLowerCase();
      const validFrom = Number(item?.valid_from || 0);
      const validUntil = Number(item?.valid_until || 0);
      const withinValidity =
        validFrom > 0 && validUntil > 0 ? now >= validFrom && now <= validUntil : true;

      return status === 'active' && withinValidity;
    });

    return active || null;
  }

  hasRenewalDiscount(plan: any, cycle: 'monthly' | 'yearly'): boolean {
    const discount = this.getActiveRenewalDiscount(plan);
    if (!discount) return false;

    const discountedAmount = cycle === 'monthly'
      ? Number(discount?.discounted_monthly_amount)
      : Number(discount?.discounted_yearly_amount);
    return !Number.isNaN(discountedAmount) && discountedAmount >= 0;
  }

  getRenewalDiscountAmount(plan: any, cycle: 'monthly' | 'yearly'): number {
    const discount = this.getActiveRenewalDiscount(plan);
    if (!discount) return cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);

    const value = cycle === 'monthly'
      ? Number(discount?.discounted_monthly_amount)
      : Number(discount?.discounted_yearly_amount);
    return Number.isNaN(value) ? 0 : value;
  }

  getOriginalPlanAmount(plan: any, cycle: 'monthly' | 'yearly'): number {
    const discount = this.getActiveRenewalDiscount(plan);
    const fallback = cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);
    if (!discount) return fallback;

    const original = cycle === 'monthly'
      ? Number(discount?.original_monthly_amount)
      : Number(discount?.original_yearly_amount);
    return Number.isNaN(original) ? fallback : original;
  }

  shouldShowStrikethrough(plan: any, cycle: 'monthly' | 'yearly'): boolean {
    const sdkPrice = this.getEffectiveSdkPrice(plan);
    const originalTotal = this.getOriginalPlanAmount(plan, cycle) + sdkPrice;
    const discountedTotal = this.getRenewalDiscountAmount(plan, cycle) + sdkPrice;
    return originalTotal > discountedTotal;
  }

  getDiscountValue(plan: any): number {
    if (this.activeTabId === 'monthly' || plan.mode === 'monthly') {
      return plan.monthly_discount || 0;
    } else if (this.activeTabId === 'annually' || plan.mode === 'annually' || this.activeTabId === 'yearly') {
      return plan.yearly_discount || 0;
    }
    return 0;
  }

  getPlanPrice(plan: any): number {
    if (this.activeTabId === 'monthly' || plan.mode === 'monthly') {
      return plan.monthly_price;
    } else if (this.activeTabId === 'annually' || plan.mode === 'annually' || this.activeTabId === 'yearly') {
      return plan.yearly_price;
    }
    return 0;
  }
  calculateDiscount(plan: any): { type: string, value: number } {
    const discountValue = this.getDiscountValue(plan);
    return {
      type: plan.discount_type,
      value: discountValue
    };
  }
  openPrivacyPolicy(): void {
    this.modal.create({
      nzTitle: this.translateService.instant('subscription-page.privacy-policy'),
      nzContent: PrivacyPolicyComponent,
      nzFooter: null,
      nzWidth: 1000,
      nzMaskClosable: true,
      nzClosable: true,
      nzWrapClassName: 'custom-modal-animate',
      nzMaskStyle: { backgroundColor: 'rgba(0,0,0,0.6)' },
      nzNoAnimation: false,
      nzBodyStyle: {
        maxHeight: '90vh',
        overflowY: 'auto'
      },
    });
  }
  openTermsConditions(): void {
    this.modal.create({
      nzTitle: this.translateService.instant('subscription-page.terms-conditions'),
      nzContent: TermsConditionComponent,
      nzFooter: null,
      nzWidth: 1000,
      nzMaskClosable: true,
      nzClosable: true,
      nzWrapClassName: 'custom-modal-animate',
      nzMaskStyle: { backgroundColor: 'rgba(0,0,0,0.6)' },
      nzNoAnimation: false,
      nzBodyStyle: {
        maxHeight: '90vh',
        overflowY: 'auto'
      },
    });
  }

}

